import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    analyticsQueue: any[];
    trackingReady: boolean;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsManagerService {
  private isInitialized = false;
  private pageViewTracked = false;
  private interactionEvents: string[] = ['scroll', 'mousemove', 'touchstart', 'click'];
  
  // IDs para Analytics
  private readonly GTM_ID = 'GTM-MZFSNS2';
  private readonly GA_MEASUREMENT_ID = 'G-LFD64VJB2R'; // ID de Google Analytics desde GTM
  private scriptsLoaded = false;
  private routerEventsActive = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router
  ) {}

  /**
   * 1. Configurar listeners sin cargar scripts (LCP)
   * 2. Preparar tracking básico
   * 3. Cargar diferido manteniendo el performance
   */
  initializeAnalytics(): void {
    if (!isPlatformBrowser(this.platformId) || this.isInitialized) {
      return;
    }

    // Configuración mínima (sin impacto en performance)
    this.setupMinimalTracking();
    
    // Listeners para carga diferida (mantiene performance)
    this.setupDeferredLoading();
    
    // Setup router tracking (lightweight)
    this.setupRouterTracking();
    
    this.isInitialized = true;
  }

  private setupMinimalTracking(): void {
    // Solo inicializar dataLayer si no existe
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    
    // gtag function básica para encolar eventos
    if (!window.gtag) {
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
    }
  }

  private setupDeferredLoading(): void {
    // Cargar en primera interacción (mantiene strategy original)
    this.interactionEvents.forEach(event => {
      document.addEventListener(event, this.loadAnalyticsScripts, { 
        once: true, 
        passive: true 
      });
    });

    // Fallback: carga después de 4 segundos si no hay interacción
    setTimeout(() => {
      if (!this.scriptsLoaded) {
        this.loadAnalyticsScripts();
      }
    }, 4000);
  }

  private loadAnalyticsScripts = (): void => {
    if (this.scriptsLoaded) return;
    
    this.scriptsLoaded = true;
    
    // Limpiar listeners
    this.interactionEvents.forEach(event => {
      document.removeEventListener(event, this.loadAnalyticsScripts);
    });

    // Cargar GTM de forma optimizada
    this.loadGTMScript();
  }

  private loadGTMScript(): void {
    // Verificar si ya existe GTM
    if (document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
      this.handleGTMReady();
      return;
    }

    // Crear script con loading optimizado
    const script = document.createElement('script');
    script.async = true;
    script.defer = true; 
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.GTM_ID}`;
    
    script.onload = () => {
      this.handleGTMReady();
    };

    // Configurar GTM antes de cargar el script
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Inicializar Google Analytics a través de GTM
    window.gtag('js', new Date());
    window.gtag('config', this.GA_MEASUREMENT_ID, {
      send_page_view: false // Evitar doble page view
    });

    // Insertar con requestIdleCallback si está disponible
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        document.head.appendChild(script);
      });
    } else {
      // Fallback para navegadores que no soportan requestIdleCallback
      setTimeout(() => {
        document.head.appendChild(script);
      }, 0);
    }
  }

  private handleGTMReady(): void {
    window.trackingReady = true;
    
    // Trackear página inicial solo después de que GTM esté listo
    this.trackInitialPageView();
    
    // Procesar eventos encolados
    this.processQueuedEvents();
    
    // Activar tracking de navegación
    this.activateRouterTracking();
  }

  private setupRouterTracking(): void {
    // Solo configurar, no activar hasta que GTM esté listo
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (this.routerEventsActive && this.pageViewTracked) {
        // Usar requestAnimationFrame para no bloquear navegación
        requestAnimationFrame(() => {
          this.trackPageView(event.urlAfterRedirects);
        });
      }
    });
  }

  private activateRouterTracking(): void {
    this.routerEventsActive = true;
  }

  private trackInitialPageView(): void {
    if (!this.pageViewTracked) {
      const currentPath = window.location.pathname + window.location.search;
      this.trackPageView(currentPath);
      this.pageViewTracked = true;
    }
  }

  private trackPageView(path: string): void {
    if (window.trackingReady && window.gtag) {
      // Usar setTimeout para no bloquear el hilo principal
      setTimeout(() => {
        window.gtag('config', this.GA_MEASUREMENT_ID, {
          page_path: path,
          page_title: document.title
        });
      }, 0);
    } else {
      // Encolar el evento si GTM no está listo
      window.analyticsQueue.push({
        type: 'pageview',
        path: path,
        title: document.title,
        timestamp: Date.now()
      });
    }
  }

  private processQueuedEvents(): void {
    if (window.analyticsQueue && window.analyticsQueue.length > 0) {
      // Procesar eventos en batches para no bloquear UI
      const processEvent = (index: number) => {
        if (index >= window.analyticsQueue.length) {
          window.analyticsQueue = []; // Limpiar cola
          return;
        }

        const event = window.analyticsQueue[index];
        
        if (event.type === 'pageview') {
          window.gtag('config', this.GA_MEASUREMENT_ID, {
            page_path: event.path,
            page_title: event.title
          });
        } else if (event.type === 'event') {
          window.gtag('event', event.name, event.parameters);
        }

        // Procesar siguiente evento en el siguiente frame
        requestAnimationFrame(() => processEvent(index + 1));
      };

      // Iniciar procesamiento
      requestAnimationFrame(() => processEvent(0));
    }
  }

  // Método público optimizado para tracking manual
  trackEvent(eventName: string, parameters: any = {}): void {
    if (window.trackingReady && window.gtag) {
      // Non-blocking event tracking
      setTimeout(() => {
        window.gtag('event', eventName, parameters);
      }, 0);
    } else {
      window.analyticsQueue.push({
        type: 'event',
        name: eventName,
        parameters: parameters,
        timestamp: Date.now()
      });
    }
  }

  // Método para forzar carga inmediata si es necesario
  forceLoadAnalytics(): void {
    if (!this.scriptsLoaded) {
      this.loadAnalyticsScripts();
    }
  }
}